import { ARTEFACT_META } from '../constants';
import {
  ArtefactType,
  ArtefactCitation,
  CaseArtefact,
  CaseOverviewStructured,
  GlossaryStructured,
  IssuesStructured,
  OpenQuestionsStructured,
  PartiesStructured,
  TimelineStructured,
} from '../types';
import { stripMarkdown } from './artefactMeta';
import { DashboardConfidenceDisplay, getConfidenceDisplay } from './confidence';
import { mergeArtefactCitations } from './citations';

export type DashboardTabId =
  | 'summary'
  | 'theory'
  | 'issues'
  | 'parties'
  | 'evidence'
  | 'timeline'
  | 'action-items'
  | 'glossary';

export interface DashboardTab {
  id: DashboardTabId;
  label: string;
  badgeCount?: number;
}

export interface DashboardTheory {
  title: string;
  party: string;
  coreProposition: string;
  narrative: string;
  supportingAllegations: DashboardTextItem[];
  supportingDocuments: string[];
  weaknesses: DashboardTextItem[];
  citations: ArtefactCitation[];
}

export interface DashboardWeakness {
  text: string;
  supportingDocuments: string[];
  citations: ArtefactCitation[];
}

export interface DashboardTextItem {
  text: string;
  citations: ArtefactCitation[];
}

export interface DashboardSummaryGroup {
  id: 'claimant' | 'respondent';
  label: string;
  itemLabel: string;
  items: DashboardTextItem[];
}

export interface DashboardQuestion {
  question: string;
  priority: 'critical' | 'high' | 'medium' | 'low';
  category: string | null;
  suggestedAction: string | null;
  relatedEntities: string[];
  relatedFiles: string[];
  citations: ArtefactCitation[];
}

export interface DashboardTheme {
  title: string;
  description: string | null;
}

export interface DashboardEvidenceBucket {
  items: DashboardTextItem[];
}

export interface DashboardParty {
  name: string;
  type: string | null;
  role: string | null;
  aliases: string[];
  description: string | null;
  mentionCount: number | null;
  sourceFiles: string[];
  citations: ArtefactCitation[];
}

export interface DashboardPartyGroup {
  id: 'claimant' | 'respondent' | 'non_aligned';
  label: string;
  items: DashboardParty[];
}

export interface DashboardTimelineItem {
  date: string;
  title: string;
  description: string | null;
  citations: ArtefactCitation[];
}

export interface DashboardModel {
  overview: {
    artefact: CaseArtefact | null;
    data: CaseOverviewStructured | null;
    summary: string;
    confidence: DashboardConfidenceDisplay;
    theories: DashboardTheory[];
    weaknesses: DashboardWeakness[];
    summaryGroups: DashboardSummaryGroup[];
  };
  questions: {
    artefact: CaseArtefact | null;
    items: DashboardQuestion[];
    orderedQuestions: DashboardQuestion[];
    guidanceItems: DashboardTextItem[];
  };
  issues: {
    artefact: CaseArtefact | null;
    data: IssuesStructured | null;
    themes: DashboardTheme[];
  };
  evidence: {
    supporting: DashboardEvidenceBucket;
    conflicting: DashboardEvidenceBucket;
    gaps: DashboardEvidenceBucket;
  };
  timeline: {
    artefact: CaseArtefact | null;
    data: TimelineStructured | null;
    items: DashboardTimelineItem[];
    isFallback: boolean;
  };
  parties: {
    artefact: CaseArtefact | null;
    data: PartiesStructured | null;
    groups: DashboardPartyGroup[];
  };
  glossary: {
    artefact: CaseArtefact | null;
    data: GlossaryStructured | null;
  };
  tabs: DashboardTab[];
}

type PartySummarySide = DashboardSummaryGroup['id'];
type PartyColumnSide = DashboardPartyGroup['id'];

function getArtefactIndex(artefacts: CaseArtefact[]) {
  return artefacts.reduce(
    (acc, artefact) => {
      acc[artefact.artefactType] = artefact;
      return acc;
    },
    {} as Partial<Record<ArtefactType, CaseArtefact>>,
  );
}

function getStructuredData<T>(artefact: CaseArtefact | null): T | null {
  if (!artefact?.structured) {
    return null;
  }

  return artefact.structured as unknown as T;
}

function dedupeStrings(items: Array<string | null | undefined>): string[] {
  const seen = new Set<string>();
  const result: string[] = [];

  items.forEach((item) => {
    const normalized = item?.trim();
    if (!normalized || seen.has(normalized)) {
      return;
    }

    seen.add(normalized);
    result.push(normalized);
  });

  return result;
}

function dedupeWeaknesses(items: DashboardWeakness[]) {
  const itemsByText = new Map<string, DashboardWeakness>();

  items.forEach((item) => {
    const normalized = item.text.trim();
    if (!normalized) {
      return;
    }

    const existing = itemsByText.get(normalized);
    if (existing) {
      existing.supportingDocuments = dedupeStrings([
        ...existing.supportingDocuments,
        ...item.supportingDocuments,
      ]);
      existing.citations = mergeArtefactCitations([...existing.citations, ...item.citations]);
      return;
    }

    itemsByText.set(normalized, {
      text: normalized,
      supportingDocuments: dedupeStrings(item.supportingDocuments),
      citations: mergeArtefactCitations(item.citations),
    });
  });

  return Array.from(itemsByText.values());
}

function createTextItem(
  text: string | null | undefined,
  citations: Array<ArtefactCitation | null | undefined> | undefined = [],
): DashboardTextItem | null {
  const normalized = text?.trim();
  if (!normalized) {
    return null;
  }

  return {
    text: normalized,
    citations: mergeArtefactCitations(citations),
  };
}

function dedupeTextItems(items: Array<DashboardTextItem | null | undefined>) {
  const itemsByText = new Map<string, DashboardTextItem>();

  items.forEach((item) => {
    if (!item) {
      return;
    }

    const normalized = item.text.trim();
    if (!normalized) {
      return;
    }

    const existing = itemsByText.get(normalized);
    if (existing) {
      existing.citations = mergeArtefactCitations([...existing.citations, ...item.citations]);
      return;
    }

    itemsByText.set(normalized, {
      text: normalized,
      citations: mergeArtefactCitations(item.citations),
    });
  });

  return Array.from(itemsByText.values());
}

function extractSummary(
  overviewArtefact: CaseArtefact | null,
  overview: CaseOverviewStructured | null,
) {
  if (overview?.summary) {
    return overview.summary;
  }

  if (!overviewArtefact?.content) {
    return '';
  }

  const executiveSummaryMatch = overviewArtefact.content.match(
    /##\s+Executive Summary\s*([\s\S]*?)(?:\n##\s+|$)/i,
  );
  const text = executiveSummaryMatch?.[1] || overviewArtefact.content;
  const paragraph = text
    .split(/\n{2,}/)
    .map((section) => stripMarkdown(section))
    .find(Boolean);

  return paragraph || '';
}

function parseQuestions(data: OpenQuestionsStructured | null): DashboardQuestion[] {
  if (!data?.questions?.length) {
    return [];
  }

  return data.questions.map((question) => ({
    question: question.question,
    priority: question.priority,
    category: question.category || null,
    suggestedAction: question.suggestedAction || null,
    relatedEntities: question.relatedEntities || [],
    relatedFiles: question.relatedFiles || [],
    citations: mergeArtefactCitations(question.citations),
  }));
}

function normalizeTheoryParty(party: string | null | undefined): PartySummarySide {
  const normalized = (party || '').toLowerCase();

  if (/(respondent|defendant)/.test(normalized)) {
    return 'respondent';
  }

  return 'claimant';
}

function inferPartySide(role: string | null | undefined): PartyColumnSide {
  const normalized = (role || '').toLowerCase();

  if (/(claimant|plaintiff|applicant|petitioner)/.test(normalized)) {
    return 'claimant';
  }

  if (/(respondent|defendant)/.test(normalized)) {
    return 'respondent';
  }

  return 'non_aligned';
}

function buildSummaryGroups(theories: DashboardTheory[]): DashboardSummaryGroup[] {
  const claimantItems = theories
    .filter((theory) => normalizeTheoryParty(theory.party) === 'claimant')
    .map((theory) => createTextItem(theory.coreProposition, theory.citations));
  const respondentItems = theories
    .filter((theory) => normalizeTheoryParty(theory.party) === 'respondent')
    .map((theory) => createTextItem(theory.coreProposition, theory.citations));

  return [
    {
      id: 'claimant',
      label: 'Claimant claims',
      itemLabel: 'claims',
      items: dedupeTextItems(claimantItems),
    },
    {
      id: 'respondent',
      label: 'Respondent defences',
      itemLabel: 'defences',
      items: dedupeTextItems(respondentItems),
    },
  ];
}

function splitThemeText(theme: string): DashboardTheme {
  const value = theme.trim();
  const separatorMatch = value.match(/\s[—-]\s/);

  if (!separatorMatch) {
    return {
      title: value,
      description: null,
    };
  }

  const [title, ...rest] = value.split(separatorMatch[0]);
  return {
    title: title.trim(),
    description: rest.join(separatorMatch[0]).trim() || null,
  };
}

function buildEvidenceBuckets(
  artefacts: CaseArtefact[],
  overview: CaseOverviewStructured | null,
  questions: DashboardQuestion[],
) {
  const supportingItems = dedupeTextItems(
    (overview?.caseTheories || []).flatMap((theory) =>
      (theory.supportingAllegations || []).map((item) => createTextItem(item, theory.citations)),
    ),
  );
  const conflictingItems = dedupeTextItems(
    (overview?.caseTheories || []).flatMap((theory) =>
      (theory.weaknesses || []).map((item) => createTextItem(item, theory.citations)),
    ),
  );
  const gapQuestionItems = questions
    .filter((question) =>
      ['missing_information', 'verification_needed', 'extraction_failure'].includes(
        question.category || '',
      ),
    )
    .map((question) => createTextItem(question.question, question.citations));
  const confidenceGapItems = artefacts.flatMap((artefact) =>
    (artefact.confidence?.gaps || []).map((gap) => createTextItem(gap.description)),
  );

  return {
    supporting: {
      items: supportingItems,
    },
    conflicting: {
      items: conflictingItems,
    },
    gaps: {
      items: dedupeTextItems([...gapQuestionItems, ...confidenceGapItems]),
    },
  };
}

function buildTimelineItems(
  timeline: TimelineStructured | null,
  overview: CaseOverviewStructured | null,
): { items: DashboardTimelineItem[]; isFallback: boolean } {
  if (timeline) {
    return {
      items: [...(timeline.events || [])]
        .sort((a, b) => a.date.localeCompare(b.date))
        .map((event) => ({
          date: event.date,
          title: event.description,
          description: event.actors?.length ? `Actors: ${event.actors.join(', ')}` : null,
          citations: mergeArtefactCitations(event.citations),
        })),
      isFallback: false,
    };
  }

  if (overview?.keyDates?.length) {
    return {
      items: [...overview.keyDates]
        .sort((a, b) => a.date.localeCompare(b.date))
        .map((event) => ({
          date: event.date,
          title: event.description,
          description: null,
          citations: [],
        })),
      isFallback: true,
    };
  }

  return {
    items: [],
    isFallback: false,
  };
}

function buildPartyGroups(parties: PartiesStructured | null): DashboardPartyGroup[] {
  const definitions: Array<Pick<DashboardPartyGroup, 'id' | 'label'>> = [
    { id: 'claimant', label: 'Claimant side' },
    { id: 'respondent', label: 'Respondent side' },
    { id: 'non_aligned', label: 'Non-aligned' },
  ];

  return definitions.map((definition) => ({
    ...definition,
    items: [...(parties?.parties || [])]
      .filter((party) => inferPartySide(party.role) === definition.id)
      .sort((left, right) => {
        const mentionDelta = (right.mentionCount || 0) - (left.mentionCount || 0);
        if (mentionDelta !== 0) {
          return mentionDelta;
        }

        return left.name.localeCompare(right.name);
      })
      .map((party) => ({
        ...party,
        citations: mergeArtefactCitations(party.citations),
      })),
  }));
}

function buildTabs(params: {
  overviewArtefact: CaseArtefact | null;
  questionsArtefact: CaseArtefact | null;
  issuesArtefact: CaseArtefact | null;
  partiesArtefact: CaseArtefact | null;
  glossaryArtefact: CaseArtefact | null;
  timelineArtefact: CaseArtefact | null;
  themeCount: number;
  issueCount: number;
  questionCount: number;
  hasTimelineFallback: boolean;
}) {
  const tabs: DashboardTab[] = [];

  if (params.overviewArtefact) {
    tabs.push({ id: 'summary', label: 'Summary' });
    tabs.push({ id: 'theory', label: 'Theory' });
  }

  if (params.issuesArtefact || params.themeCount > 0) {
    tabs.push({
      id: 'issues',
      label: 'Issues and themes',
    });
  }

  if (params.partiesArtefact) {
    tabs.push({ id: 'parties', label: 'Parties' });
  }

  if (params.overviewArtefact) {
    tabs.push({ id: 'evidence', label: 'Evidence' });
  }

  if (params.timelineArtefact || params.hasTimelineFallback) {
    tabs.push({ id: 'timeline', label: 'Timeline' });
  }

  if (params.questionsArtefact) {
    tabs.push({
      id: 'action-items',
      label: 'Action items',
    });
  }

  if (params.glossaryArtefact) {
    tabs.push({ id: 'glossary', label: 'Glossary' });
  }

  return tabs;
}

function orderQuestionsByPriority(questions: DashboardQuestion[]) {
  const priorities: DashboardQuestion['priority'][] = ['critical', 'high', 'medium', 'low'];

  return priorities.flatMap((priority) =>
    questions.filter((question) => question.priority === priority),
  );
}

export function buildDashboardModel(artefacts: CaseArtefact[]): DashboardModel {
  const sortedArtefacts = [...artefacts].sort(
    (a, b) =>
      (ARTEFACT_META[a.artefactType]?.order ?? 99) - (ARTEFACT_META[b.artefactType]?.order ?? 99),
  );
  const index = getArtefactIndex(sortedArtefacts);

  const overviewArtefact = index['case-overview'] || null;
  const overview = getStructuredData<CaseOverviewStructured>(overviewArtefact);
  const questionsArtefact = index['open-questions'] || null;
  const questions = getStructuredData<OpenQuestionsStructured>(questionsArtefact);
  const issuesArtefact = index.issues || null;
  const issues = getStructuredData<IssuesStructured>(issuesArtefact);
  const timelineArtefact = index.timeline || null;
  const timeline = getStructuredData<TimelineStructured>(timelineArtefact);
  const partiesArtefact = index['parties-and-players'] || null;
  const parties = getStructuredData<PartiesStructured>(partiesArtefact);
  const glossaryArtefact = index['glossary-and-aliases'] || null;
  const glossary = getStructuredData<GlossaryStructured>(glossaryArtefact);

  const parsedQuestions = parseQuestions(questions);
  const orderedQuestions = orderQuestionsByPriority(parsedQuestions);
  const guidanceItems = orderedQuestions
    .map((question) => createTextItem(question.suggestedAction, question.citations))
    .filter((item): item is DashboardTextItem => Boolean(item));
  const theories = (overview?.caseTheories || []).map((theory) => ({
    title: theory.title,
    party: theory.party,
    coreProposition: theory.coreProposition,
    narrative: theory.narrative,
    supportingAllegations: (theory.supportingAllegations || [])
      .map((item) => createTextItem(item, theory.citations))
      .filter((item): item is DashboardTextItem => Boolean(item)),
    supportingDocuments: theory.supportingDocuments || [],
    weaknesses: (theory.weaknesses || [])
      .map((item) => createTextItem(item, theory.citations))
      .filter((item): item is DashboardTextItem => Boolean(item)),
    citations: mergeArtefactCitations(theory.citations),
  }));
  const theoryWeaknesses = dedupeWeaknesses(
    theories.flatMap((theory) =>
      theory.weaknesses.map((weakness) => ({
        text: weakness.text,
        supportingDocuments: theory.supportingDocuments || [],
        citations: weakness.citations,
      })),
    ),
  );
  const weaknesses =
    theoryWeaknesses.length > 0
      ? theoryWeaknesses
      : orderedQuestions.map((question) => ({
          text: question.question,
          supportingDocuments: question.relatedFiles || [],
          citations: question.citations,
        }));
  const summaryGroups = buildSummaryGroups(theories);
  const timelineItems = buildTimelineItems(timeline, overview);
  const partyGroups = buildPartyGroups(parties);
  const themes = (overview?.themes || []).map(splitThemeText);
  const evidence = buildEvidenceBuckets(sortedArtefacts, overview, orderedQuestions);
  const tabs = buildTabs({
    overviewArtefact,
    questionsArtefact,
    issuesArtefact,
    partiesArtefact,
    glossaryArtefact,
    timelineArtefact,
    themeCount: themes.length,
    issueCount: issues?.issues?.length || 0,
    questionCount: orderedQuestions.length,
    hasTimelineFallback: timelineItems.isFallback,
  });

  return {
    overview: {
      artefact: overviewArtefact,
      data: overview,
      summary: extractSummary(overviewArtefact, overview),
      confidence: getConfidenceDisplay(overviewArtefact?.confidence),
      theories,
      weaknesses,
      summaryGroups,
    },
    questions: {
      artefact: questionsArtefact,
      items: parsedQuestions,
      orderedQuestions,
      guidanceItems: dedupeTextItems(guidanceItems),
    },
    issues: {
      artefact: issuesArtefact,
      data: issues,
      themes,
    },
    evidence,
    timeline: {
      artefact: timelineArtefact,
      data: timeline,
      items: timelineItems.items,
      isFallback: timelineItems.isFallback,
    },
    parties: {
      artefact: partiesArtefact,
      data: parties,
      groups: partyGroups,
    },
    glossary: {
      artefact: glossaryArtefact,
      data: glossary,
    },
    tabs,
  };
}
